import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Plus, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { proFormaInvoiceService } from '../../services/proforma-invoice.service';
import { ProFormaStatus } from '../../types';
import { GenerateProFormaModal } from './GenerateProFormaModal';

export const ProFormaInvoicesPage: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<ProFormaStatus | 'ALL'>('ALL');
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: allInvoices = [], isLoading: isLoadingAll } = useQuery({
    queryKey: ['proFormaInvoices'],
    queryFn: proFormaInvoiceService.getAll,
    enabled: selectedStatus === 'ALL',
  });

  const { data: statusInvoices = [], isLoading: isLoadingStatus } = useQuery({
    queryKey: ['proFormaInvoices', selectedStatus],
    queryFn: () => proFormaInvoiceService.getByStatus(selectedStatus as ProFormaStatus),
    enabled: selectedStatus !== 'ALL',
  });

  const invoices = selectedStatus === 'ALL' ? allInvoices : statusInvoices;
  const isLoading = selectedStatus === 'ALL' ? isLoadingAll : isLoadingStatus;

  const issueMutation = useMutation({
    mutationFn: (id: number) => proFormaInvoiceService.issue(id),
    onSuccess: () => {
      toast.success('Pro forma invoice issued successfully');
      queryClient.invalidateQueries({ queryKey: ['proFormaInvoices'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to issue pro forma invoice');
    },
  });

  const getStatusBadgeVariant = (status: ProFormaStatus) => {
    switch (status) {
      case ProFormaStatus.DRAFT:
        return 'default';
      case ProFormaStatus.ISSUED:
        return 'info';
      case ProFormaStatus.ACCEPTED:
        return 'success';
      case ProFormaStatus.REJECTED:
        return 'error';
      case ProFormaStatus.EXPIRED:
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <Card
        title="Pro Forma Invoices"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant={selectedStatus === 'ALL' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setSelectedStatus('ALL')}
            >
              All
            </Button>
            <Button
              variant={selectedStatus === ProFormaStatus.DRAFT ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setSelectedStatus(ProFormaStatus.DRAFT)}
            >
              Draft
            </Button>
            <Button
              variant={selectedStatus === ProFormaStatus.ISSUED ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setSelectedStatus(ProFormaStatus.ISSUED)}
            >
              Issued
            </Button>
            <Button
              variant={selectedStatus === ProFormaStatus.ACCEPTED ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setSelectedStatus(ProFormaStatus.ACCEPTED)}
            >
              Accepted
            </Button>
            <Button size="sm" onClick={() => setIsGenerateModalOpen(true)}>
              <Plus size={16} className="mr-2" />
              Generate
            </Button>
          </div>
        }
      >
        {isLoading && <div className="text-sm text-gray-600">Loading...</div>}

        {!isLoading && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pro Forma #</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Distance</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={9}>
                      No pro forma invoices found.
                    </td>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.proFormaNumber}</TableCell>
                      <TableCell>{format(parseISO(invoice.issueDate), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{invoice.clientName}</div>
                          {invoice.clientContact && (
                            <div className="text-xs text-gray-500">{invoice.clientContact}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{invoice.origin}</div>
                          <div className="text-gray-500">→ {invoice.destination}</div>
                        </div>
                      </TableCell>
                      <TableCell>{invoice.distanceKm} km</TableCell>
                      <TableCell className="font-medium">{formatCurrency(invoice.totalAmount)}</TableCell>
                      <TableCell>{format(parseISO(invoice.validUntil), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {invoice.status === ProFormaStatus.DRAFT && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => issueMutation.mutate(invoice.id)}
                            loading={issueMutation.isPending}
                          >
                            <Send size={14} className="mr-1" />
                            Issue
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <GenerateProFormaModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
      />
    </div>
  );
};
